import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import DOMPurify from "dompurify";
import { FileText } from "lucide-react";

export default function StaticPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setPage(null);
    api.get(`/pages/${slug}`)
      .then((r) => { setPage(r.data); document.title = `${r.data.title} — The School Reports`; })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="static-page-not-found">
        <div className="text-6xl mb-4">🧭</div>
        <h1 className="font-serif-display text-3xl text-[#1F2937]">Halaman tidak ditemukan</h1>
        <p className="mt-3 text-[#4B5563]">Halaman yang Anda cari mungkin sudah dihapus atau belum dipublikasikan.</p>
      </div>
    );
  }

  if (!page) return <div className="max-w-3xl mx-auto p-10 text-[#4B5563]">Memuat…</div>;

  const raw = page.body || "";
  const isHtml = /<(p|h[1-6]|ul|ol|li|blockquote|strong|em|a|br|hr|img|pre|code|aside|figure|figcaption)\b/i.test(raw);
  const cleanHtml = isHtml
    ? DOMPurify.sanitize(raw, {
        USE_PROFILES: { html: true },
        ADD_TAGS: ["figure", "figcaption", "aside"],
        ADD_ATTR: ["data-pull-quote", "data-image-figure"],
      })
    : null;

  return (
    <div data-testid="static-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1E3A8A] mb-3">
        <FileText size={12} /> Halaman
      </div>
      <h1 data-testid="static-page-title" className="font-serif-display text-4xl lg:text-5xl text-[#1B2B4D] leading-tight">{page.title}</h1>
      {isHtml ? (
        <div
          data-testid="static-page-body"
          // Static pages inherit the Plus Jakarta Sans body font from :root while
          // headings inside prose remain Fraunces serif — same design system as
          // the Journal detail page.
          className="mt-6 prose max-w-none text-[17px] leading-[1.85] text-[#1F2937]
                     prose-headings:font-serif-display prose-headings:text-[#1B2B4D]
                     prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-3
                     prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-2
                     prose-p:my-4
                     prose-blockquote:border-l-4 prose-blockquote:border-[#D2673E]
                     prose-blockquote:bg-[#FDF6F0] prose-blockquote:italic prose-blockquote:text-[#4B5563] prose-blockquote:rounded-r-md
                     prose-a:text-[#D2673E] prose-a:no-underline hover:prose-a:underline
                     prose-img:rounded-xl prose-img:border prose-img:border-[#EAE7E0]
                     prose-code:text-[#BE5730] prose-code:bg-[#FBFAF6] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                     prose-pre:bg-[#1B2B4D] prose-pre:text-[#F3F1ED] prose-pre:rounded-xl prose-pre:p-4"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
      ) : (
        <div className="mt-6 text-[#1F2937] text-lg leading-relaxed whitespace-pre-wrap" data-testid="static-page-body">
          {raw}
        </div>
      )}
      <div className="mt-10 pt-6 border-t border-[#EAE7E0] text-xs text-[#4B5563]">
        Terakhir diperbarui: {new Date(page.updated_at || page.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
      </div>
    </div>
  );
}
